import type { Msg, Session } from '@halo/chat-sdk-react';
import { MessageStatus, MsgPayloadType } from '@halo/chat-sdk';
import { MsgGroupUpdateAdmins } from './msg-group-update-admins';
import { MsgGroupUpdateDescription } from './msg-group-update-description';
import { MsgGroupRestricted } from './msg-group-restricted';
import { MsgGroupUnrestricted } from './msg-group-unrestricted';
import { MsgGroupSendMsgAdmin } from './msg-group-send-msg-admin';
import { MsgGroupSendMsgMember } from './msg-group-send-msg-member';
import { MsgGroupRestrictedActions } from './msg-group-restricted-actions';
import { MsgGroupRestrictedActionsOff } from './msg-group-restricted-actions-off';
import { MsgGroupRestrictedActionsOffAll } from './msg-group-restricted-actions-off-all';
import { MsgGroupScreenshotWarning } from './msg-group-screenshot-warning';
import { MsgGroupScreenRecordingWarning } from './msg-group-screen-recording-warning';
import {
  MsgSticker,
  MsgText,
  MsgMediaCall,
  MsgMediaHangUp,
  MsgMediaCallTimeout,
  MsgMediaCallCancelled,
  MsgMediaCallRejected,
  MsgMediaCallNetworkError,
  MsgGroupCreate,
  MsgGroupUpdate,
  MsgGroupDisband,
  MsgGroupInvite,
  MsgGroupRemoveMember,
  MsgGroupMemberQuit,
  MsgGroupMediaClose,
  MsgGroupUpdateJoin,
  MsgGroupUpdateInfo,
  MsgExpireDuration,
  MsgLuckyBoxClaimed,
  MsgTransfer,
  MsgImage,
  MsgFile,
  MsgLuckyBox,
  MsgEncryptFailed,
  MsgUnknown,
  MsgCard,
} from './index';

export interface MsgsItemProps {
  message: Msg | undefined;
  session: Session | undefined;
}

export const MsgsItem = memo(function MsgsItem({ message, session }: MsgsItemProps) {
  if (!session || !message) {
    return null;
  }

  switch (message?.status) {
    case MessageStatus.FailedEncrypt:
    case MessageStatus.FailedDecrypt:
      return <MsgEncryptFailed message={message} session={session} />;
  }

  switch (message.payload_type) {
    case MsgPayloadType.TEXT:
      return <MsgText message={message} session={session} />;
    case MsgPayloadType.STICKER:
      return <MsgSticker message={message} session={session} />;
    case MsgPayloadType.IMAGE:
      return <MsgImage message={message} session={session} />;
    case MsgPayloadType.FILE:
      return <MsgFile message={message} session={session} />;
    case MsgPayloadType.MEDIA_AUDIO_CALL:
      return <MsgMediaCall message={message} session={session} />;
    case MsgPayloadType.CALL_HANG_UP:
      return <MsgMediaHangUp message={message} session={session} />;
    case MsgPayloadType.CALL_TIMEOUT:
      return <MsgMediaCallTimeout message={message} session={session} />;
    case MsgPayloadType.CALL_CANCELLED:
      return <MsgMediaCallCancelled message={message} session={session} />;
    case MsgPayloadType.CALL_REJECTED:
      return <MsgMediaCallRejected message={message} session={session} />;
    case MsgPayloadType.CALL_NETWORK_ERROR:
      return <MsgMediaCallNetworkError message={message} session={session} />;
    case MsgPayloadType.GROUP_CREATE:
      return <MsgGroupCreate message={message} />;
    case MsgPayloadType.GROUP_UPDATE_NAME:
      return <MsgGroupUpdate message={message} />;
    case MsgPayloadType.GROUP_UPDATE_DESCRIPTION:
      return <MsgGroupUpdateDescription message={message} />;
    case MsgPayloadType.GROUP_RESTRICTED:
      return <MsgGroupRestricted message={message} />;
    case MsgPayloadType.GROUP_UNRESTRICTED:
      return <MsgGroupUnrestricted message={message} />;
    case MsgPayloadType.GROUP_ADMIN_ONLY_SEND:
      return <MsgGroupSendMsgAdmin message={message} />;
    case MsgPayloadType.GROUP_EXCEPTION_SEND:
      return <MsgGroupSendMsgMember message={message} />;
    case MsgPayloadType.GROUP_RESTRICTED_MODE_ON:
      return <MsgGroupRestrictedActions message={message} />;
    case MsgPayloadType.GROUP_RESTRICTED_MODE_OFF:
      return <MsgGroupRestrictedActionsOff message={message} />;
    case MsgPayloadType.GROUP_RESTRICTED_MODE_OFF_All:
      return <MsgGroupRestrictedActionsOffAll message={message} />;
    case MsgPayloadType.GROUP_SCREENSHOT_WARNING:
      return <MsgGroupScreenshotWarning message={message} />;
    case MsgPayloadType.GROUP_SCREEN_RECORDING_WARNING:
      return <MsgGroupScreenRecordingWarning message={message} />;
    case MsgPayloadType.GROUP_DISBAND:
      return <MsgGroupDisband message={message} />;
    case MsgPayloadType.GROUP_INVITE:
      return <MsgGroupInvite message={message} />;
    case MsgPayloadType.GROUP_REMOVE_MEMBER:
      return <MsgGroupRemoveMember message={message} />;
    case MsgPayloadType.GROUP_MEMBER_QUIT:
      return <MsgGroupMemberQuit message={message} />;
    case MsgPayloadType.GROUP_MEDIA_CLOSE:
      return <MsgGroupMediaClose message={message} session={session} />;
    case MsgPayloadType.GROUP_UPDATE_JOIN:
    case MsgPayloadType.GROUP_JOIN_SUCCESS:
      return <MsgGroupUpdateJoin message={message} />;
    case MsgPayloadType.GROUP_TRANSFER_OWNER:
      return <MsgGroupUpdateInfo message={message} />;
    case MsgPayloadType.GROUP_UPDATE_ADMINS:
      return <MsgGroupUpdateAdmins message={message} />;
    case MsgPayloadType.EXPIRE_DURATION:
      return <MsgExpireDuration message={message} />;
    case MsgPayloadType.LUCKY_BOX_CLAIMED:
      return <MsgLuckyBoxClaimed message={message} />;
    case MsgPayloadType.LUCKY_BOX:
      return <MsgLuckyBox message={message} session={session} />;
    case MsgPayloadType.TRANSFER:
      return <MsgTransfer message={message} session={session} />;
    case MsgPayloadType.CARD:
      return <MsgCard message={message} session={session} />;
  }

  return <MsgUnknown message={message} session={session} />;
});
